import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureInPictureIcon from '@mui/icons-material/PictureInPicture';
import DownloadIcon from '@mui/icons-material/Download';
import { formatTime } from './videoPlayerUtils';

const VideoPlayerControls = ({
  isPlaying, currentTime, duration, volume, isMuted, isFullscreen,
  showMenu, setShowMenu, onPlayPause, onProgressClick, onVolumeChange,
  onMuteToggle, onFullscreen, onPictureInPicture, onSeekArrow, onDownload,
  progressRole = 'slider'
}) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu, setShowMenu]);

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="custom-video-player__controls">
      <div
        className="custom-video-player__progress-container"
        onClick={onProgressClick}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); onSeekArrow?.('right'); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); onSeekArrow?.('left'); }
        }}
        role={progressRole}
        tabIndex={0}
        aria-label="Video progress"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={currentTime}
        aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
      >
        <div className="custom-video-player__progress-bar">
          <div className="custom-video-player__progress-fill" style={{ width: `${progressPercent}%` }} />
          <div className="custom-video-player__progress-thumb" style={{ left: `${progressPercent}%` }} />
        </div>
      </div>

      <div className="custom-video-player__buttons">
        <button className="custom-video-player__btn" onClick={onPlayPause}
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </button>

        <div className="custom-video-player__volume-group">
          <button className="custom-video-player__btn" onClick={onMuteToggle}
            title={isMuted ? 'Unmute (M)' : 'Mute (M)'} aria-label={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </button>
          <div className="custom-video-player__volume-slider-wrapper">
            <input type="range" min="0" max="1" step="0.1" value={isMuted ? 0 : volume}
              onChange={onVolumeChange} className="custom-video-player__volume-slider"
              title="Volume" aria-label="Volume" />
          </div>
        </div>

        <div className="custom-video-player__time">
          <span>{formatTime(currentTime)}</span>
          <span className="custom-video-player__time-separator">/</span>
          <span>{formatTime(duration)}</span>
        </div>

        <div className="custom-video-player__spacer" />

        <div className="custom-video-player__menu-wrapper" ref={menuRef}>
          <button className="custom-video-player__btn" onClick={() => setShowMenu(!showMenu)}
            title="More options" aria-label="More options">
            <MoreVertIcon />
          </button>
          {showMenu && (
            <div className="custom-video-player__menu">
              {onDownload && (
                <>
                  <button className="custom-video-player__menu-item" onClick={onDownload}>
                    <DownloadIcon /><span>Download</span>
                  </button>
                  <div className="custom-video-player__menu-divider" />
                </>
              )}
              <button className="custom-video-player__menu-item" onClick={onPictureInPicture}>
                <PictureInPictureIcon /><span>Picture in Picture</span>
              </button>
              <div className="custom-video-player__menu-divider" />
              <button className="custom-video-player__menu-item" onClick={onFullscreen}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                <span>{isFullscreen ? 'Exit Fullscreen' : 'Maximize'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

VideoPlayerControls.propTypes = {
  isPlaying: PropTypes.bool.isRequired,
  currentTime: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  volume: PropTypes.number.isRequired,
  isMuted: PropTypes.bool.isRequired,
  isFullscreen: PropTypes.bool.isRequired,
  showMenu: PropTypes.bool.isRequired,
  setShowMenu: PropTypes.func.isRequired,
  onPlayPause: PropTypes.func.isRequired,
  onProgressClick: PropTypes.func.isRequired,
  onVolumeChange: PropTypes.func.isRequired,
  onMuteToggle: PropTypes.func.isRequired,
  onFullscreen: PropTypes.func.isRequired,
  onPictureInPicture: PropTypes.func.isRequired,
  onSeekArrow: PropTypes.func,
  onDownload: PropTypes.func,
  progressRole: PropTypes.string,
};

export default VideoPlayerControls;
